import React from "react";
import { Check, Infinity as InfinityIcon } from "lucide-react";
import { Button } from "../ui/button";

// Personalised copy for the Infinity tier (uses the user's natal signature when known).
export function planPresentation(p, user) {
  const tag = user?.natal?.personalized_tag;
  const cz = user?.natal?.chinese_zodiac;
  const lp = user?.natal?.life_path;
  const owned = (user?.vip_lifetime && p.infinity) || (user?.tier === "blueprint" && p.id === "natal_blueprint") || (user?.tier === "solar_return" && p.id === "solar_return");
  if (!(p.infinity && tag)) return { owned, tag, displayName: p.infinity ? "Cosmic Infinity" : p.name, tagline: p.tagline, features: p.features };
  return {
    owned, tag,
    displayName: `${lp ?? 9} Life-Path · ${tag} Infinity`,
    tagline: `Your ${tag} lifetime seat`,
    features: [
      `Permanent ${tag} chart access · all features`,
      `Unlimited scans forever — every city, every line`,
      `Gold VIP badge · Year of the ${cz || "Cosmos"} halo in chat`,
    ],
  };
}

export function PricingCard({ p, user, busy, onBuy }) {
  const Icon = p.icon;
  const { owned, tag, displayName, tagline, features } = planPresentation(p, user);
  const disabled = busy === p.id || user?.vip_lifetime;
  return (
    <div
      key={p.id}
      data-testid={p.testId}
      className={`relative rounded-xl transition-transform hover:-translate-y-1 ${p.highlight ? "md:scale-[1.02]" : ""}`}
    >
      {/* Glow ring */}
      <div
        className={`absolute inset-0 rounded-xl bg-gradient-to-br ${p.ring} opacity-70 blur-[8px]`}
        aria-hidden
      />
      {/* Card body */}
      <div
        className={`relative rounded-xl p-4 h-full flex flex-col border ${p.highlight ? "border-[#C8A96E]" : "border-[#C8A96E]/40"}`}
        style={{
          background: p.highlight
            ? "linear-gradient(160deg, #1C1622 0%, #4A3356 55%, #4A3356 100%)"
            : "linear-gradient(160deg, #1C1622 0%, #261E2E 100%)",
        }}
      >
        {p.highlight && (
          <div className="absolute -top-2.5 left-4 bg-[#C8A96E] text-[#15111A] text-[9px] uppercase tracking-widest px-2 py-0.5 font-bold shadow-[0_0_16px_rgba(200,169,110,0.6)]">
            Most Popular
          </div>
        )}
        {p.infinity && (
          <div className="absolute -top-2.5 right-4 border border-[#C8A96E] bg-[#15111A] text-[#C8A96E] text-[9px] uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
            <InfinityIcon size={10} /> Infinity
          </div>
        )}

        <div className="flex items-start justify-between">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: `${p.accent}22`, border: `1px solid ${p.accent}88` }}
          >
            <Icon size={17} style={{ color: p.accent }} />
          </div>
          {owned && (
            <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-[#9CBF8E]/60 text-[#9CBF8E]">
              Owned
            </span>
          )}
        </div>

        <div className="mt-3.5">
          <div className="text-[9px] uppercase tracking-[0.28em] text-[#E8DFD3]/70">{tagline}</div>
          <div className="font-heading text-lg mt-0.5" data-testid={`plan-name-${p.id}`}>{displayName}</div>
          {p.infinity && tag && (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A96E]/50 bg-[#C8A96E]/10 px-2 py-0.5"
              data-testid="infinity-signature-ribbon"
            >
              <span className="text-[8px] uppercase tracking-[0.28em] text-[#A89C98]">Signature</span>
              <span className="text-[10px] font-bold text-[#C8A96E]" data-testid="infinity-signature-tag">
                {tag}
              </span>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-end gap-1.5">
          <div className="font-mono-scanner text-4xl text-[#C8A96E] leading-none">{p.price}</div>
          <div className={`mb-1 uppercase tracking-widest ${p.id === "natal_blueprint" ? "text-[10px] text-[#A89C98]" : "text-xs font-bold text-[#C8A96E]"}`} data-testid={`price-suffix-${p.id}`}>{p.priceSuffix}</div>
        </div>

        <ul className="mt-3.5 space-y-1.5 flex-1">
          {features.map((f) => (
            <li key={f} className="flex gap-2 text-[11px] leading-snug text-white/85">
              <Check size={13} className="text-[#9CBF8E] shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Button
          data-testid={`buy-${p.id}`}
          disabled={disabled}
          onClick={() => onBuy(p.id)}
          className={`w-full mt-4 rounded-none h-9 uppercase tracking-widest text-[11px] font-bold ${
            p.highlight || p.infinity
              ? "bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] shadow-[0_0_18px_rgba(200,169,110,0.5)]"
              : "bg-[#C8A96E]/90 text-[#15111A] hover:bg-[#C8A96E]"
          }`}
        >
          {owned ? "Unlocked" : busy === p.id ? "Processing…" : p.ctaLabel}
        </Button>
      </div>
    </div>
  );
}
